import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { SlidersHorizontal, X, MapPin, Grid, List, Map, Crosshair, Loader } from 'lucide-react';
import ListingCard from '../components/listing/ListingCard';
import { listingService } from '../services/listingService';
import { categoryService } from '../services/categoryService';
import { useLanguage } from '../context/LanguageContext';

const ListingsMap = lazy(() => import('../components/map/ListingsMap'));

const CITIES = ['Berlin', 'München', 'Hamburg', 'Frankfurt', 'Köln', 'Stuttgart', 'Düsseldorf', 'Leipzig'];
const CONDITIONS_TR = ['Sıfır', 'Mükemmel', 'Çok İyi', 'İyi', 'Kabul Edilebilir'];

export default function Search() {
  const [searchParams] = useSearchParams();
  const { t, tCat, tCond } = useLanguage();
  const [showFilters, setShowFilters]   = useState(false);
  const [viewMode, setViewMode]         = useState('grid'); // 'grid' | 'list' | 'map'
  const [categories, setCategories]     = useState([]);
  const [results, setResults]           = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [nearbyMode, setNearbyMode]     = useState(false);
  const [userLocation, setUserLocation] = useState(null); // { lat, lng }
  const [locating, setLocating]         = useState(false);

  const q             = searchParams.get('q') || '';
  const categoryParam = searchParams.get('category') || '';

  const [filters, setFilters] = useState({
    category: categoryParam,
    condition: '',
    city: '',
    minPrice: '',
    maxPrice: '',
    sortBy: 'newest',
  });

  const setFilter = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    categoryService.getAll().then(setCategories).catch(() => {});
  }, []);

  const fetchResults = useCallback(async () => {
    if (viewMode === 'map') return; // map handles its own data
    setLoading(true);
    try {
      const data = await listingService.getAll({
        search: q || undefined,
        categorySlug: filters.category || undefined,
        condition: filters.condition || undefined,
        city: filters.city || undefined,
        minPrice: filters.minPrice || undefined,
        maxPrice: filters.maxPrice || undefined,
        sortBy: filters.sortBy,
        pageSize: 50,
      });
      setResults(data?.items || []);
      setTotal(data?.totalCount || data?.total || 0);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [q, filters, viewMode]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  // Yakınımdaki ürünler
  function handleNearby() {
    if (!navigator.geolocation) return;
    if (nearbyMode) {
      // toggle off
      setNearbyMode(false);
      setUserLocation(null);
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        setNearbyMode(true);
        setViewMode('map');
        setLocating(false);
      },
      () => setLocating(false)
    );
  }

  const activeFiltersCount = [filters.category, filters.condition, filters.city, filters.minPrice, filters.maxPrice].filter(Boolean).length;
  const categoryLabel = filters.category ? tCat(filters.category) : '';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-apple-gray-dark">
            {q ? `"${q}" ${t('search.results_for')}` : categoryLabel || t('search.all_listings')}
          </h1>
          <p className="text-sm text-apple-gray-mid mt-0.5">
            {viewMode === 'map'
              ? 'Harita görünümü — kaydırarak veya yakınlaştırarak keşfedin'
              : loading ? t('search.loading') : `${total} ${t('search.listings_found')}`}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Yakınımdaki ürünler */}
          <button
            onClick={handleNearby}
            disabled={locating}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
              nearbyMode
                ? 'bg-apple-blue text-white border-apple-blue'
                : 'border-gray-200 text-apple-gray-dark hover:border-apple-blue'
            }`}
          >
            {locating
              ? <Loader className="w-4 h-4 animate-spin" />
              : <Crosshair className="w-4 h-4" />
            }
            Yakınımdakiler
          </button>

          <button
            onClick={() => setShowFilters(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${showFilters ? 'bg-apple-blue text-white border-apple-blue' : 'border-gray-200 text-apple-gray-dark hover:border-apple-blue'}`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {t('search.filters_btn')}
            {activeFiltersCount > 0 && <span className="bg-apple-red text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{activeFiltersCount}</span>}
          </button>

          {viewMode !== 'map' && (
            <select value={filters.sortBy} onChange={e => setFilter('sortBy', e.target.value)} className="input-field py-2 flex-1 min-w-0 sm:w-44 sm:flex-none">
              <option value="newest">{t('search.sort_newest')}</option>
              <option value="price_asc">{t('search.sort_price_asc')}</option>
              <option value="price_desc">{t('search.sort_price_desc')}</option>
              <option value="views">{t('search.sort_views')}</option>
            </select>
          )}

          {/* View toggle: grid / list / map */}
          <div className="flex border border-gray-200 rounded-xl overflow-hidden shrink-0">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-apple-blue text-white' : 'hover:bg-apple-gray text-apple-gray-mid'}`}
              title="Izgara"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-apple-blue text-white' : 'hover:bg-apple-gray text-apple-gray-mid'}`}
              title="Liste"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`p-2 transition-colors ${viewMode === 'map' ? 'bg-apple-blue text-white' : 'hover:bg-apple-gray text-apple-gray-mid'}`}
              title="Harita"
            >
              <Map className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-apple-gray rounded-2xl p-5 mb-6 animate-slide-up">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-medium text-apple-gray-mid mb-1.5">{t('search.filter_category')}</label>
              <select value={filters.category} onChange={e => setFilter('category', e.target.value)} className="input-field py-2 text-xs">
                <option value="">{t('search.filter_all')}</option>
                {categories.map(c => <option key={c.id} value={c.slug}>{tCat(c.slug)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-apple-gray-mid mb-1.5">{t('search.filter_condition')}</label>
              <select value={filters.condition} onChange={e => setFilter('condition', e.target.value)} className="input-field py-2 text-xs">
                <option value="">{t('search.filter_all')}</option>
                {CONDITIONS_TR.map(c => <option key={c} value={c}>{tCond(c)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-apple-gray-mid mb-1.5">{t('search.filter_city')}</label>
              <select value={filters.city} onChange={e => setFilter('city', e.target.value)} className="input-field py-2 text-xs">
                <option value="">{t('search.filter_all')}</option>
                {CITIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-apple-gray-mid mb-1.5">{t('search.filter_min_price')}</label>
              <input type="number" value={filters.minPrice} onChange={e => setFilter('minPrice', e.target.value)} placeholder="0" className="input-field py-2 text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium text-apple-gray-mid mb-1.5">{t('search.filter_max_price')}</label>
              <input type="number" value={filters.maxPrice} onChange={e => setFilter('maxPrice', e.target.value)} placeholder="9999" className="input-field py-2 text-xs" />
            </div>
          </div>
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => setFilters({ category: '', condition: '', city: '', minPrice: '', maxPrice: '', sortBy: 'newest' })}
              className="flex items-center gap-1.5 text-xs text-apple-red hover:underline"
            >
              <X className="w-3 h-3" /> {t('search.filter_clear')}
            </button>
            <button onClick={() => setShowFilters(false)} className="btn-primary text-sm px-4 py-2">{t('search.filter_apply')}</button>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {activeFiltersCount > 0 && viewMode !== 'map' && (
        <div className="flex flex-wrap gap-2 mb-4">
          {filters.category && <Chip label={`${t('search.chip_category')}: ${categoryLabel}`} onRemove={() => setFilter('category', '')} />}
          {filters.condition && <Chip label={`${t('search.chip_condition')}: ${tCond(filters.condition)}`} onRemove={() => setFilter('condition', '')} />}
          {filters.city && <Chip label={`${t('search.chip_city')}: ${filters.city}`} onRemove={() => setFilter('city', '')} />}
          {filters.minPrice && <Chip label={`${t('search.chip_min')}: ${filters.minPrice}€`} onRemove={() => setFilter('minPrice', '')} />}
          {filters.maxPrice && <Chip label={`${t('search.chip_max')}: ${filters.maxPrice}€`} onRemove={() => setFilter('maxPrice', '')} />}
        </div>
      )}

      {/* MAP VIEW */}
      {viewMode === 'map' && (
        <Suspense fallback={<div className="w-full rounded-2xl bg-apple-gray animate-pulse" style={{ height: 520 }} />}>
          <ListingsMap
            style={{ height: 520 }}
            nearbyMode={nearbyMode}
            userLocation={userLocation}
          />
        </Suspense>
      )}

      {/* GRID / LIST VIEW */}
      {viewMode !== 'map' && (
        loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-apple-gray rounded-3xl h-64 animate-pulse" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-4xl mb-4">🔍</p>
            <h3 className="text-xl font-semibold text-apple-gray-dark mb-2">{t('search.empty_title')}</h3>
            <p className="text-apple-gray-mid mb-6">{t('search.empty_subtitle')}</p>
            <Link to="/search" className="btn-secondary">{t('search.empty_btn')}</Link>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5' : 'space-y-4'}>
            {results.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        )
      )}
    </div>
  );
}

function Chip({ label, onRemove }) {
  return (
    <span className="flex items-center gap-1.5 bg-apple-blue/10 text-apple-blue text-xs font-medium px-3 py-1 rounded-full">
      {label}
      <button onClick={onRemove} className="hover:text-apple-red transition-colors"><X className="w-3 h-3" /></button>
    </span>
  );
}
